import React, { useEffect, useReducer } from 'react';
import { useWeb3React } from '@web3-react/core';
import { convertToBiobit } from '../utils';
import appCache from '../utils/cache';
import { actionTypes } from './actionTypes';
import {
	configureFallbackWeb3,
	getZarelaCurrentDay,
	getGasPrice,
	getEthPrice,
	configureWeb3,
} from './actions';
import { injectedConnector } from '../connectors';

const appInitialState = {
	error: null,

	biobitBalance: 'Hidden Info',
	etherBalance: 'Hidden Info',

	gas: {},
	ethPrice: null,

	fallbackWeb3Instance: null,
	contract: null,
	isMobile: null,
	isMobileSearchModalShow: null,
	guideIsOpen: null,
	notificationCount: null,

	zarelaCurrent: null,
};

const mainContext = React.createContext(appInitialState);

const AppProvider = ({ children }) => {
	const { library, account, active, activate } = useWeb3React();

	const [appState, dispatch] = useReducer((state, action) => {
		const { type } = action;

		switch (type) {
			case actionTypes.SET_FALLBACK_WEB3:
				return {
					...state,
					fallbackWeb3Instance: action.payload,
				};
			case actionTypes.SET_CONTRACT:
				return {
					...state,
					contract: action.payload,
				};
			case actionTypes.SET_BBIT_BALANCE:
				return {
					...state,
					biobitBalance: action.payload,
				};
			case actionTypes.SET_ZARELA_CURRENT_DAY:
				return {
					...state,
					zarelaCurrentDay: action.payload,
				};
			case actionTypes.SET_ETHER_BALANCE:
				return {
					...state,
					etherBalance: action.payload,
				};
			case actionTypes.SET_ERROR:
				return {
					...state,
					error: action.payload,
				};
			case actionTypes.SET_GAS:
				return {
					...state,
					gas: action.payload,
				};
			case actionTypes.SET_CLIENT_DEVICE:
				return {
					...state,
					isMobile: action.payload,
				};
			case actionTypes.SET_MOBILE_SEARCH_MODAL_SHOW:
				return {
					...state,
					isMobileSearchModalShow: action.payload,
				}
			case actionTypes.SET_GUIDE_IS_OPEN:
				return {
					...state,
					guideIsOpen: action.payload,
				};
			case actionTypes.SET_NOTIFICATION_COUNT:
				return {
					...state,
					notificationCount: action.payload,
				};
			case actionTypes.SET_ETH_PRICE:
				return {
					...state,
					ethPrice: action.payload,
				};
			case actionTypes.SET_OLD_DATA_FORM:
				return {
					...state,
					oldDataForm: action.payload,
				};
			default:
				return state;
		}
	}, appInitialState);

	useEffect(() => {
		getGasPrice(dispatch);
		// to detect device anywhere in the component tree
		dispatch({
			type: actionTypes.SET_CLIENT_DEVICE,
			payload: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent
			)
				? true
				: false,
		});
		dispatch({
			type: actionTypes.SET_MOBILE_SEARCH_MODAL_SHOW,
			payload: false
		})
		// to trigger guide to open from header in each page
		dispatch({
			type: actionTypes.SET_GUIDE_IS_OPEN,
			payload: false,
		});

		dispatch({
			type: actionTypes.SET_NOTIFICATION_COUNT,
			payload: 0,
		});
	}, []);

	useEffect(() => {
		// we use a fallback web3 provider to be able to fetch data regardless
		// of user's wallet connection to dApp (since @web3-react does not populate
		// library unless there's a wallet connected to dApp)
		if (library) {
			configureWeb3(dispatch, library);
		} else {
			configureFallbackWeb3(dispatch);
		}
	}, [library]);

	useEffect(() => {
		if (appState.contract) {
			getZarelaCurrentDay(dispatch, appState.contract);
		}
	}, [appState.contract]);

	useEffect(() => {
		// populate homepage sidebar values
		if (account === undefined) {
			dispatch({
				type: actionTypes.SET_BBIT_BALANCE,
				payload: appInitialState.biobitBalance,
			});
			dispatch({
				type: actionTypes.SET_ETHER_BALANCE,
				payload: appInitialState.etherBalance,
			});
		}
	}, [account]);

	useEffect(() => {
		// populate homepage sidebar values
		if (account !== undefined && appState.contract) {
			// Check cache first
			const cachedBBitBalance = appCache.get(account, 'bbitBalance');
			if (cachedBBitBalance) {
				dispatch({
					type: actionTypes.SET_BBIT_BALANCE,
					payload: cachedBBitBalance,
				});
				return;
			}

			// Use promise-based call instead of callback for Web3 v4 compatibility
			appState.contract.methods.balanceOf(account).call()
				.then((result) => {
					const balance = convertToBiobit(+result);
					
					// Cache the result
					appCache.set(account, 'bbitBalance', balance);
					
					dispatch({
						type: actionTypes.SET_BBIT_BALANCE,
						payload: balance,
					});
				})
				.catch((error) => {
					console.error('Error getting BBIT balance:', error.message || error);
					console.error('Contract address:', process.env.REACT_APP_ZARELA_CONTRACT_ADDRESS);
					console.error('Account:', account);
				});
		}
	}, [account, appState.contract]);

	useEffect(() => {
		const activeWeb3 = library || appState.fallbackWeb3Instance;
		if (activeWeb3 && appState.contract) {
			if (account) {
				// Check cache first
				const cachedBalance = appCache.get(account, 'etherBalance');
				if (cachedBalance) {
					dispatch({
						type: actionTypes.SET_ETHER_BALANCE,
						payload: cachedBalance,
					});
					return;
				}

				// Add delay to prevent overwhelming MetaMask with RPC calls
				const timeoutId = setTimeout(() => {
					activeWeb3.eth
						.getBalance(account)
						.then(function (result) {
							const balance = Number(activeWeb3.utils.fromWei(result, 'ether')).toFixed(4);
							
							// Cache the result
							appCache.set(account, 'etherBalance', balance);
							
							dispatch({
								type: actionTypes.SET_ETHER_BALANCE,
								payload: balance,
							});
						})
						.catch((error) => {
							console.error('Error getting balance:', error.message);
							// Don't break the app if balance call fails
						});
				}, 1000); // 1 second delay to prevent circuit breaker

				return () => clearTimeout(timeoutId);
			}
		}
	}, [account, library, appState.contract, appState.fallbackWeb3Instance]);

	// manually activate metamask in case it's connected
	useEffect(() => {
		if (window.ethereum?.selectedAddress && active === false) {
			activate(injectedConnector);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, window.ethereum?.selectedAddress]);

	// Clear cache when wallet changes
	useEffect(() => {
		appCache.clear();
	}, [account]);

	useEffect(() => {
		getEthPrice(dispatch);
	}, [appState.gas]);

	useEffect(() => {
		if (localStorage.getItem('create_request_values')) {
			dispatch({
				type: actionTypes.SET_OLD_DATA_FORM,
				payload: JSON.parse(localStorage.getItem('create_request_values')),
			});
		}
	}, []);

	return (
		<mainContext.Provider
			value={{
				appState,
				dispatch,
			}}
		>
			{children}
		</mainContext.Provider>
	);
};

export { mainContext, AppProvider };
